import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal, Box, SelectChangeEvent, Stack } from '@mui/material'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  addRisquesToAction,
  getRisqueByActionId,
  removeRisquesFromAction,
} from '../../redux/risques/risqueActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import SelectRisque from './SelectRisque'
import UseAlert from '../../components/common/alert'
import {
  DetailButton,
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPagePill,
  PRIORITY_PILL,
  RISK_HORIZON_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { formatTitle } from '../../utils/formatTitle'
import '../../components/common/ListPageTable.css'

interface RisqueListProps {
  ActionId: string
  MissionId: string
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  borderRadius: '12px',
}

const RisqueListByAction: React.FC<RisqueListProps> = ({
  ActionId,
  MissionId,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const risquesList = useSelector(
    (state: RootState) => state.risque.sourceRisques
  )
  const loading = useSelector((state: RootState) => state.risque.loading)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [SourceRisque, setSourceRisque] = useState<string[]>([])
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  useEffect(() => {
    if (ActionId) dispatch(getRisqueByActionId(ActionId))
  }, [dispatch, ActionId])

  const rows = useMemo(() => {
    const list = Array.isArray(risquesList) ? risquesList : []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((r: any) =>
      (r.RisqueName || '').toLowerCase().includes(q)
    )
  }, [risquesList, search])

  const handleDeleteClick = async (_id: string) => {
    try {
      await dispatch(
        removeRisquesFromAction({
          SourceRisque: { risqueId: _id },
          _id: ActionId,
        })
      )
      dispatch(getRisqueByActionId(ActionId))
      setAlert({
        open: true,
        message: t('alerts.deleteRisquesFromAction.success'),
        severity: 'success',
      })
    } catch {
      setAlert({
        open: true,
        message: t('alerts.deleteRisquesFromAction.error'),
        severity: 'error',
      })
    }
  }

  const handleAddRisquesToAction = async () => {
    try {
      if (SourceRisque.length && ActionId) {
        await dispatch(
          addRisquesToAction({
            SourceRisque,
            _id: ActionId,
          })
        )
        await dispatch(getRisqueByActionId(ActionId))
        setAlert({
          open: true,
          message: t('alerts.addRisquesToAction.success'),
          severity: 'success',
        })
        setOpen(false)
        setSourceRisque([])
      } else {
        setAlert({
          open: true,
          message: t('alerts.addRisquesToAction.emptyFields'),
          severity: 'error',
        })
      }
    } catch {
      setAlert({
        open: true,
        message: t('alerts.addRisquesToAction.error'),
        severity: 'error',
      })
    }
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />
      <DetailEntityPanel
        listTitle={t('risks_list', { defaultValue: 'Risks list' })}
        searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
        searchValue={search}
        onSearchChange={setSearch}
        addLabel={t('buttons.addRisk', { defaultValue: 'Add New Risk' })}
        onAdd={() => setOpen(true)}
        canAdd={canManageMission(roles)}
      >
        <DetailTable<any>
          loading={loading && !rows.length}
          loadingLabel={t('loading')}
          emptyTitle={t('missionDetailsPage.empty.title', {
            defaultValue: 'Nothing here yet',
          })}
          emptyMessage={t('taskDetailsPage.empty.risks', {
            defaultValue: 'No risk is linked to this task yet.',
          })}
          rows={rows}
          rowKey={(r) => (r._id || r.id) as string}
          columns={[
            {
              key: 'name',
              header: t('Name', { defaultValue: 'Name' }),
              render: (r) => (
                <NavLink
                  to={`/risque-detail/${r._id}`}
                  className="incyia-name-link"
                >
                  <ListPageDescription
                    value={
                      r.RisqueName ? formatTitle(r.RisqueName) : undefined
                    }
                    tone="primary"
                  />
                </NavLink>
              ),
            },
            {
              key: 'desc',
              header: t('Description', { defaultValue: 'Description' }),
              render: (r) => <ListPageDescription value={r.Description} />,
            },
            {
              key: 'mission',
              header: t('Mission Name', { defaultValue: 'Mission name' }),
              render: (r) => (
                <ListPageDescription
                  value={
                    r.MissionName ? formatTitle(r.MissionName) : undefined
                  }
                />
              ),
            },
            {
              key: 'crit',
              header: t('Criticality', { defaultValue: 'Criticality' }),
              render: (r) => {
                const c = Number(r.Criticality)
                const style = PRIORITY_PILL[c] ?? PRIORITY_PILL[4]
                return (
                  <ListPagePill
                    label={
                      t(`priority.${c}`, {
                        defaultValue: style.label,
                      }) || style.label
                    }
                    bg={style.bg}
                    color={style.color}
                  />
                )
              },
            },
            {
              key: 'prio',
              header: t('Priority', { defaultValue: 'Priority' }),
              render: (r) => {
                const p = Number(r.Priority)
                const style = RISK_HORIZON_PILL[p] ?? RISK_HORIZON_PILL[3]
                return (
                  <ListPagePill
                    label={t(`prioritRisk.${p}`, {
                      defaultValue: String(p),
                    })}
                    bg={style.bg}
                    color={style.color}
                  />
                )
              },
            },
          ]}
          actionsHeader={t('Actions', { defaultValue: 'Action' })}
          renderActions={(r) => (
            <DetailRowActions
              roles={roles}
              onEdit={() => navigate(`/risque/${r._id}`)}
              onDelete={() => handleDeleteClick(r._id as string)}
              deleteItem={{
                entityType: t('risks', { defaultValue: 'Risk' }),
                name: r.RisqueName ? formatTitle(r.RisqueName) : undefined,
                details: [
                  {
                    label: t('deleteConfirm.fields.mission'),
                    value: r.MissionName
                      ? formatTitle(r.MissionName)
                      : '—',
                  },
                  {
                    label: t('deleteConfirm.fields.status'),
                    value: r.Status || '—',
                  },
                ],
              }}
            />
          )}
        />
      </DetailEntityPanel>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          <h4 style={{ margin: '0 0 16px' }}>{t('buttons.addRisk')}</h4>
          <Stack spacing={2}>
            <SelectRisque
              value={SourceRisque}
              onChange={(e: SelectChangeEvent<string[]>) =>
                setSourceRisque(e.target.value as string[])
              }
              MissionId={MissionId}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <DetailButton onClick={handleAddRisquesToAction}>
                {t('buttons.add')}
              </DetailButton>
              <DetailButton variant="ghost" onClick={() => setOpen(false)}>
                {t('buttons.cancel')}
              </DetailButton>
            </div>
          </Stack>
        </Box>
      </Modal>
    </>
  )
}

export default RisqueListByAction
